import config from "../config";
import request, {
  requestOptions,
  requestOptionsFormData,
} from "../utils/request";

const baseUrl = `${config.API_ROOT}/ai`;

const Ai = {
  async getAiSettings() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/settings`;
    return request(url, options);
  },
  async getAiSettingsSystem() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/settings-system`;
    return request(url, options);
  },
  async putAiSettingsByField(field, value, location) {
    const options = await requestOptions("PUT", { value, location }, true);
    const url = `${baseUrl}/settings/field/${field}`;
    return request(url, options);
  },
  async putAiSettingsByFieldV2({ field, value, embeddingId }) {
    const options = await requestOptions("PUT", { value, embeddingId }, true);
    const url = `${baseUrl}/settings/field/${field}`;
    return request(url, options);
  },
  async getFreeText() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/free-text`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async createUpdateFreeText({ embeddingId, embededContent, updateTrain }) {
    const method = embeddingId ? "PATCH" : "POST";
    const options = await requestOptions(
      method,
      { embededContent, updateTrain },
      true,
    );
    const url = embeddingId
      ? `${baseUrl}/free-text/${embeddingId}`
      : `${baseUrl}/free-text`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async getCustomerContext() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/customer-context`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async createUpdateCustomerContext({ embeddingId, embededContent }) {
    const method = embeddingId ? "PATCH" : "POST";
    const options = await requestOptions(method, { embededContent }, true);
    const url = embeddingId
      ? `${baseUrl}/customer-context/${embeddingId}`
      : `${baseUrl}/customer-context`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async getFiles() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/files`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async processFile(formData) {
    const options = await requestOptionsFormData(formData, true);
    const url = `${baseUrl}/files`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async patchFile({ embeddingId, active }) {
    const options = await requestOptions("PATCH", { active }, true);
    const url = `${baseUrl}/files/${embeddingId}`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async deleteFile({ embeddingId, fileName }) {
    const options = await requestOptions("DELETE", { fileName }, true);
    const url = `${baseUrl}/files/${embeddingId}`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async getWebsite() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/website`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async patchUrl({ embeddingId, active }) {
    const options = await requestOptions("PATCH", { active }, true);
    const url = `${baseUrl}/website/${embeddingId}`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async deleteUrl({ embeddingId }) {
    const options = await requestOptions("DELETE", {}, true);
    const url = `${baseUrl}/website/${embeddingId}`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async singleCrawling({ url, companyId, source = undefined, async = false }) {
    const options = await requestOptions(
      "POST",
      { url, companyId, source, async },
      true,
    );
    const path = `${baseUrl}/website`;
    const { data, err } = await request(path, options);
    if (err) {
      throw err;
    }
    return data;
  },
  async resetCrawling({ url, companyId }) {
    const options = await requestOptions("PUT", { url, companyId }, true);
    const path = `${baseUrl}/website`;
    const { data, err } = await request(path, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async getAiStatus() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/status`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async updateTrainingStatus() {
    const options = await requestOptions("PATCH", {}, true);
    const url = `${baseUrl}/status`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async getProviders() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/providers`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async createUpdateFunction({ functionId, description, name, active }) {
    const method = functionId ? "PATCH" : "POST";
    const options = await requestOptions(method, { description, name, active }, true);
    const url = functionId
      ? `${baseUrl}/functions/${functionId}`
      : `${baseUrl}/functions`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async getHelpRules() {
    const options = await requestOptions("GET", {}, true);
    const url = `${baseUrl}/help-rules`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return { defaultRules: data?.defaultRules, customRules: data?.customRules };
  },
  async createUpdateHelpRule({ ruleId, description, name, active }) {
    const method = ruleId ? "PATCH" : "POST";
    const options = await requestOptions(method, { description, name, active }, true);
    const url = ruleId
      ? `${baseUrl}/help-rules/${ruleId}`
      : `${baseUrl}/help-rules`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async deleteHelpRule({ ruleId }) {
    const options = await requestOptions("DELETE", {}, true);
    const url = `${baseUrl}/help-rules/${ruleId}`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }

    return data;
  },
  async toggleHelpRulesEnable({ active }) {
    const options = await requestOptions("PATCH", { active }, true);
    const url = `${baseUrl}/help-rules/enable`;
    const { data, err } = await request(url, options);
    if (err) {
      throw err;
    }
    return data;
  },
  async createSystemPrompt({
    agentType = undefined,
    companyId,
    companyName = "",
    aboutCompany = "",
    otherContext = "",
    locale = "en",
  }) {
    const options = await requestOptions(
      "POST",
      { agentType, companyId, companyName, aboutCompany, otherContext, locale },
      true,
    );
    const path = `${baseUrl}/system-prompt`;
    const { data, err } = await request(path, options);
    if (err) {
      throw err;
    }

    return data;
  },
};

export default Ai;
